/**
 * CustomAssetManager - Handles custom character and monster assets
 * 
 * This service manages the storage and retrieval of custom D&D 5e assets that users create.
 * It provides an interface between the UI components and backend JSON file storage.
 * 
 * Key features:
 * - Save/load custom characters and monsters to backend JSON files
 * - Convert between internal data format and Asset Browser display format
 * - Track asset metadata (creation time, last modified, etc.)
 * - Support for overwriting existing assets
 * - Caching for performance optimization
 * - Integration with existing Asset Browser system
 * 
 * STORAGE FORMAT:
 * - localStorage key: 'ascendant_custom_assets'
 * - Value: JSON object with { characters: [], monsters: [] }
 * - Each asset: { id, name, type, baseTemplate, customData, artwork, createdAt, lastModified }
 * 
 * INTEGRATION:
 * - Used by CharacterEditor for saving custom characters
 * - Used by AssetBrowser to display custom assets alongside preloaded ones
 * - Provides unified interface for asset management
 */

import { DEBUG_ENABLED } from '../utils/DebugLogger';
import backendUrlResolver from '../utils/BackendUrlResolver';
import { extractCanonicalKey, canonicalKeyToUrl } from '../utils/pathNormalizer';
import { normalizeUploadedAsset } from '../utils/uploadAssetUrlResolver';
import { assertCustomEntityCreationAllowed } from './EntitlementService';

class CustomAssetManager {
  constructor() {
    this.storageKey = 'ascendant_custom_assets'; // Keep for backwards compatibility
    // @date_of_change: 2025-09-25 - Use dynamic backend URL resolver
    this.apiUrl = null; // Will be resolved dynamically
    this.assetCache = null;
    this.charactersCache = null;
    this.monstersCache = null;
    this.conversionCache = new Map();
    // date_of_change: 2025-08-24
    // Add a cache version for conversion caching (referenced by toAssetBrowserFormat)
    // This ensures we don't hit undefined when composing cache keys
    this.cacheVersion = this.cacheVersion || '1';
    
    // Initialize storage if needed
    this.initializeStorage();
    
    // Migrate from localStorage to backend on first load
    this.migrateFromLocalStorage();
  }

  /**
   * Get dynamic backend URL using BackendUrlResolver
   * @date_of_change: 2025-09-25 - Replace hardcoded localhost with resolver
   */
  async getBackendBase() {
    return await backendUrlResolver.getBackendUrl();
  }

  /**
   * Get asset base URL for images and files
   * @date_of_change: 2025-09-27 - Use S3 CDN for all assets
   */
  async getAssetBase() {
    // Use S3 CDN for all assets instead of backend
    return process.env.REACT_APP_ASSET_CDN_BASE || 'https://virtualcombatsimulator.s3.amazonaws.com/assets';
  }

  /**
   * Get dynamic backend URL using BackendUrlResolver
   * @date_of_change: 2025-09-25 - Removed localhost fallback to fix mixed content errors
   */
  async getApiUrl() {
    if (!this.apiUrl) {
      this.apiUrl = await backendUrlResolver.getBackendUrl();
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Resolved backend URL:', this.apiUrl);
    }
    return this.apiUrl;
  }

  /**
   * initializeStorage
   * date_of_change: 2025-08-24
   * 
   * Purpose:
   * - Provide a safe initialization path so construction never throws.
   * - Ensure localStorage contains a well-formed container object when used as fallback.
   * - Prepare simple in-memory caches.
   *
   * Notes:
   * - This method intentionally avoids any network calls to keep constructor sync-safe.
   */
  initializeStorage() {
    try {
      // Ensure a consistent container exists in localStorage
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(this.storageKey) : null;
      if (!raw) {
        const seed = { characters: [], monsters: [] };
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(this.storageKey, JSON.stringify(seed));
        }
      } else {
        // Validate structure and repair if needed (no duplicate keys per JSON rules)
        try {
          const parsed = JSON.parse(raw);
          if (!parsed || typeof parsed !== 'object') {
            throw new Error('Invalid storage payload');
          }
          if (!Array.isArray(parsed.characters)) parsed.characters = [];
          if (!Array.isArray(parsed.monsters)) parsed.monsters = [];
          // Re-write only if we had to repair
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(this.storageKey, JSON.stringify(parsed));
          }
        } catch (e) {
          // If parsing fails, reset to a known-good structure
          const seed = { characters: [], monsters: [] };
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(this.storageKey, JSON.stringify(seed));
          }
        }
      }

      // Initialize simple in-memory caches
      this.assetCache = null;
      this.charactersCache = null;
      this.monstersCache = null;

      // Non-throwing debug log
      try { if (DEBUG_ENABLED) console.debug('[CustomAssetManager] initializeStorage: completed'); } catch (_) {}
    } catch (_) {
      // Swallow any init errors to avoid constructor crashes
    }
  }

  /**
   * Delete a custom asset (character or monster) by id with backend-first strategy
   * date_of_change: 2025-08-24
   * - Tries DELETE on backend when available
   * - Falls back to removing from localStorage when backend is unavailable
   * - Clears in-memory caches after deletion
   * @param {string|Object} target - Asset id string or object containing at least { id, type }
   * @returns {Promise<boolean>} - True if deletion completed locally
   */
  async deleteCustomAsset(target) {
    try {
      // Normalize input to { id, assetType }
      const id = typeof target === 'string' ? target : target?.id;
      if (!id) throw new Error('deleteCustomAsset: missing id');

      // 🔧 ENHANCED: Better asset type determination with debugging
      // @date_of_change 2025-09-04 - Fixed monster deletion issue
      const rawType = (typeof target === 'object' && target?.type) ? String(target.type).toLowerCase() : '';
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] deleteCustomAsset debugging:', {
        id,
        target,
        rawType,
        targetType: typeof target === 'object' ? target?.type : 'N/A'
      });
      
      let assetType = '';
      
      // Method 1: Check explicit type field
      if (rawType === 'character') assetType = 'characters';
      else if (rawType === 'monster') assetType = 'monsters';
      
      // Method 2: Check ID prefix pattern
      if (!assetType) {
        if (id.startsWith('custom_character_')) assetType = 'characters';
        else if (id.startsWith('custom_monster_')) assetType = 'monsters';
      }
      
      // Method 3: Backend lookup (NEW - more reliable)
      if (!assetType) {
        try {
          // @date_of_change: 2025-09-21 - Use apiFetch for consistent backend URL resolution
          const { apiFetch } = await import('../utils/apiFetch.js');

          // Try to find the asset in backend data
          try {
            const response = await apiFetch(`/api/monsters/${id}`);
            if (response.ok) {
              assetType = 'monsters';
              if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Found asset in backend monsters');
            } else {
              const charResponse = await apiFetch(`/api/characters/${id}`);
              if (charResponse.ok) {
                assetType = 'characters';
                if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Found asset in backend characters');
              }
            }
          } catch (apiError) {
            if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Backend API calls failed:', apiError.message);
          }
        } catch (error) {
          if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Backend lookup failed, trying localStorage');
        }
      }
      
      // Method 4: LocalStorage fallback
      if (!assetType) {
        const all = this.getAllCustomAssetsFromLocalStorage();
        if (all.characters.find(a => a.id === id)) assetType = 'characters';
        else if (all.monsters.find(a => a.id === id)) assetType = 'monsters';
      }
      
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Final assetType determination:', assetType);
      if (!assetType) throw new Error('deleteCustomAsset: unable to determine asset type');

      // @date_of_change 2025-08-30 - Use backend for persistent deletion
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Using backend for persistent deletion');
      
      // Attempt backend deletion first
      try {
        const { apiFetch } = await import('../utils/apiFetch.js');
        const resp = await apiFetch(`/api/${assetType}/${encodeURIComponent(id)}`, { method: 'DELETE' });
        if (resp && resp.ok) {
          // Success on backend; also remove local cache copy
          this._removeFromLocalStorage(assetType, id);
          this._clearCachesAfterMutation();
          return true;
        }
      } catch (_) {
        // Backend not available; fall back to localStorage
      }

      // Local fallback deletion
      this._removeFromLocalStorage(assetType, id);
      this._clearCachesAfterMutation();
      return true;
    } catch (error) {
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Failed to delete custom asset:', error);
      throw error;
    }
  }

  /**
   * Remove an asset from localStorage by type and id
   * @private
   * date_of_change: 2025-08-24
   */
  _removeFromLocalStorage(assetType, id) {
    const allAssets = this.getAllCustomAssetsFromLocalStorage();
    if (!allAssets[assetType]) return;
    const filtered = allAssets[assetType].filter(a => a.id !== id);
    allAssets[assetType] = filtered;
    localStorage.setItem(this.storageKey, JSON.stringify(allAssets));
  }

  /**
   * Clear in-memory caches after a mutation
   * @private
   * date_of_change: 2025-08-24
   */
  _clearCachesAfterMutation() {
    this.assetCache = null;
    this.charactersCache = null;
    this.monstersCache = null;
    this._cachedAssets = null;
    this.clearConversionCache();
  }

  /**
   * migrateFromLocalStorage
   * date_of_change: 2025-08-24
   * 
   * Purpose:
   * - Non-blocking, best-effort migration of existing localStorage assets to backend.
   * - Designed to never throw synchronously (constructor safety).
   *
   * Behavior:
   * - Schedules an async task; if backend is unreachable, it quietly no-ops.
   */
  migrateFromLocalStorage() {
    // Defer to next tick so constructor remains synchronous and safe
    setTimeout(async () => {
      try {
        // Skip health check in development to avoid console errors when backend isn't running
        // date_of_change: 2025-08-30 - Disabled health check in development mode
        if (process.env.NODE_ENV === 'development') {
          if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Skipping backend health check in development mode');
          return;
        }
        
        // Quick health probe to see if backend is available (production only)
        const backendBase = await this.getApiUrl();
        const healthUrl = `${backendBase}/api/health`;
        let backendOk = false;
        try {
          const resp = await fetch(healthUrl, { method: 'GET' });
          backendOk = resp && resp.ok;
        } catch (error) {
          // Silently handle connection errors - they're expected when backend is down
          backendOk = false;
        }

        if (!backendOk) {
          // Backend not available; keep data in localStorage silently
          try { if (DEBUG_ENABLED) console.debug('[CustomAssetManager] migrateFromLocalStorage: backend unavailable, skipped'); } catch (_) {}
          return;
        }

        // Load any locally stored assets
        const assets = this.getAllCustomAssetsFromLocalStorage();
        const hasLocal = assets && (assets.characters.length > 0 || assets.monsters.length > 0);
        if (!hasLocal) {
          return; // nothing to migrate
        }

        // Best-effort migration of each asset type to backend
        // @date_of_change: 2025-10-06 - Use apiFetch for Clerk auth token support
        const { apiFetch } = await import('../utils/apiFetch.js');
        const migrateOne = async (asset, type) => {
          try {
            const endpoint = `/api/${type}`; // characters | monsters
            const payload = { ...(asset.customData || asset) };
            // Ensure IDs and names are present
            payload.id = asset.id || payload.id;
            payload.name = asset.name || payload.name || 'Unnamed';
            // Preserve timestamps if present
            if (asset.createdAt) payload.createdAt = asset.createdAt;
            if (asset.lastModified) payload.lastModified = asset.lastModified;
            await apiFetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
          } catch (_) {
            // Ignore individual failures; keep going
          }
        };

        await Promise.all([
          ...assets.characters.map(c => migrateOne(c, 'characters')),
          ...assets.monsters.map(m => migrateOne(m, 'monsters'))
        ]);

        try { if (DEBUG_ENABLED) console.debug('[CustomAssetManager] migrateFromLocalStorage: completed best-effort migration'); } catch (_) {}
      } catch (_) {
        // Never throw from migration
      }
    }, 0);
  }

  /**
   * Save a custom asset (character or monster)
   * @param {Object} asset - The asset to save
   * @returns {Promise} - Resolves when saved
   */
  async saveCustomAsset(asset) {
    try {
      // Generate ID if not present
      // date_of_change: 2025-08-24 - Pass safe name/type to generateAssetId to avoid undefined errors
      if (!asset.id) {
        const safeType = asset.type === 'character' ? 'character' : 'monster';
        const safeName = (asset.name && typeof asset.name === 'string' && asset.name.trim().length > 0)
          ? asset.name
          : 'custom_asset';
        asset.id = this.generateAssetId(safeName, safeType);
      }
      
      // Add timestamps
      const now = new Date().toISOString();
      asset.lastModified = now;
      if (!asset.createdAt) {
        asset.createdAt = now;
      }
      
      // Determine asset type and endpoint
      const assetType = asset.type === 'character' ? 'characters' : 'monsters';
      
      // Prepare data for backend
      // 🔧 CRITICAL SUCCESS: Data Access Pattern Fix
      // @date_of_change 2025-09-04 - SOLUTION to character data loss in save process
      // 
      // ROOT CAUSE: Previous code looked for asset.customData, but rich character data
      // from patched files is stored in asset.canonicalData after normalization.
      // This caused loss of classFeatures, items, feats, and actions during save.
      // 
      // THE FIX: Check canonicalData FIRST, then customData as fallback
      // This preserves complete D&D 5e character data through the save pipeline
      const dataToSave = asset.canonicalData || asset.customData || asset;
      dataToSave.id = asset.id;
      dataToSave.name = asset.name || dataToSave.name;
      dataToSave.lastModified = asset.lastModified;
      dataToSave.createdAt = asset.createdAt;

      // @date_of_change: 2025-09-22 - Normalize artwork to relative '/uploads/...'
      // date_of_change: 2026-03-18
      // rule_reference: Commercial upload delivery must persist canonical upload paths, not raw S3 URLs,
      // so browser reads continue working even if bucket CORS or hostnames change.
      const normalizeArtworkPath = (p) => {
        if (!p || typeof p !== 'string') return p;
        const canonicalKey = extractCanonicalKey(p);
        if (canonicalKey) {
          return canonicalKeyToUrl(canonicalKey) || p;
        }
        if (p.startsWith('/uploads/')) return p;
        try {
          const cached = window.localStorage.getItem('backend_url_cache');
          if (cached) {
            const { url } = JSON.parse(cached);
            if (url && p.startsWith(url)) {
              return p.slice(url.length);
            }
          }
        } catch (_) {}
        const idx = p.indexOf('/uploads/');
        return idx !== -1 ? p.slice(idx) : p;
      };
      if (dataToSave.artwork) {
        dataToSave.artwork = normalizeArtworkPath(dataToSave.artwork);
      }
      
      // @date_of_change 2025-08-30 - Use backend for persistent file-based storage instead of localStorage
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Saving to backend file system for persistence');
      
      if (DEBUG_ENABLED) console.debug(`[CustomAssetManager] Saving ${asset.type} to backend:`, {
        id: dataToSave.id,
        name: dataToSave.name,
        hp: dataToSave.hp,
        maxHp: dataToSave.maxHp,
        abilities: dataToSave.abilities
      });
      
      // Save to backend (dynamic backend URL via apiFetch)
      const { apiFetch } = await import('../utils/apiFetch.js');
      const response = await apiFetch(`/api/${assetType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave)
      });
      
      if (!response.ok) {
        throw new Error(`Backend save failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // Clear caches when assets change
      this.assetCache = null;
      this.charactersCache = null;
      this.monstersCache = null;
      
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Successfully saved to backend:', result.data.name);
      return result.data;
      
    } catch (error) {
      // @date_of_change: 2025-10-05 - Enhanced error logging to diagnose backend save failures
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Failed to save to backend:', {
        error: error.message,
        assetType: asset.type,
        assetName: asset.name,
        assetId: asset.id,
        stack: error.stack
      });

      // Backend not available - this is normal in development without backend running
      // date_of_change: 2025-08-30 - Made error logging less alarming for expected backend unavailability
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Backend not available, using localStorage fallback');
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Authentication failed - user may not be logged in');
      } else {
        if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Unexpected backend error - falling back to localStorage');
      }

      // Fallback to localStorage if backend fails
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Saving to localStorage instead...');
      return this.saveToLocalStorage(asset);
    }
  }
  
  /**
   * Fallback save to localStorage
   * @private
   */
  saveToLocalStorage(asset) {
    try {
      const allAssets = this.getAllCustomAssetsFromLocalStorage();
      const assetType = asset.type === 'character' ? 'characters' : 'monsters';
      const existingIndex = allAssets[assetType].findIndex(a => a.id === asset.id);
      
      if (existingIndex !== -1) {
        allAssets[assetType][existingIndex] = asset;
      } else {
        allAssets[assetType].push(asset);
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(allAssets));
      return Promise.resolve(asset);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get all custom assets for Asset Browser display
   * @param {boolean} useCache - Whether to use cached results
   * @returns {Object} - { characters: [], monsters: [] }
   */
  async getAllCustomAssets(useCache = true) {
    try {
      // Use static variable for memoization
      if (useCache && this._cachedAssets) {
        return this._cachedAssets;
      }

      // Try to load from backend first (if available)
      try {
        // @date_of_change 2025-08-30 - Re-enable backend calls to use proper JSON file storage
        if (DEBUG_ENABLED) console.debug('[CustomAssetManager] 🔍 DEBUGGING: Using backend API for persistent storage');
        
        // @date_of_change: 2025-09-21 - Use apiFetch for consistent backend URL resolution and error handling
        let monstersResponse = null;
        let charactersResponse = null;

        try {
          const { apiFetch } = await import('../utils/apiFetch.js');

          const [monstersResult, charactersResult] = await Promise.all([
            apiFetch('/api/monsters').catch(err => {
              if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Monsters API call failed:', err.message);
              return null;
            }),
            apiFetch('/api/characters').catch(err => {
              if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Characters API call failed:', err.message);
              return null;
            })
          ]);

          monstersResponse = monstersResult;
          charactersResponse = charactersResult;
        } catch (importError) {
          if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Failed to import apiFetch, using fallback:', importError.message);

          // Fallback to direct fetch if apiFetch import fails
          const [monstersResult, charactersResult] = await Promise.all([
            fetch(`${this.apiUrl}/api/monsters`).catch(() => null),
            fetch(`${this.apiUrl}/api/characters`).catch(() => null)
          ]);

          monstersResponse = monstersResult;
          charactersResponse = charactersResult;
        }
        
        if (DEBUG_ENABLED) console.debug('[CustomAssetManager] 🔍 Backend responses:', {
          monstersOk: monstersResponse?.ok,
          charactersOk: charactersResponse?.ok,
          monstersStatus: monstersResponse?.status,
          charactersStatus: charactersResponse?.status
        });
        
        if (monstersResponse && charactersResponse && monstersResponse.ok && charactersResponse.ok) {
          const monstersData = await monstersResponse.json();
          const charactersData = await charactersResponse.json();
          
          if (DEBUG_ENABLED) console.debug('[CustomAssetManager] 🔍 Raw backend data:', {
            monstersData,
            charactersData
          });
          
          const assets = {
            monsters: monstersData.data || monstersData || [],
            characters: charactersData.data || charactersData || [],
            version: '1.0.0'
          };
          
          // Convert backend format to frontend format
          // date_of_change: 2025-08-24 - Lift name/artwork from nested data for SRD-style wrappers
          assets.monsters = assets.monsters.map(m => ({
            ...m,
            // Ensure top-level name/artwork for UI components that read these directly
            name: m.name || (m.data && m.data.name) || 'Unnamed Monster',
            artwork: m.artwork || (m.data && m.data.artwork) || m.artwork,
            type: 'monster',
            source: 'custom', // @date_of_change 2025-09-12 - CRITICAL FIX: Mark custom monsters for proper artwork handling
            canonicalData: m, // @date_of_change 2025-09-14 - ARCHITECTURAL FIX: Use canonicalData for consistency with characters
            customData: m     // Keep customData as fallback for compatibility
          }));
          
          // date_of_change: 2025-09-12 - CRITICAL FIX: Make backend character format consistent with saveCanonicalCharacter
          // Store in canonicalData to match newly uploaded characters, ensuring consistent data access
          assets.characters = assets.characters.map(c => {
            // @date_of_change 2025-09-12 - DEBUG: Inspect what backend provides for rich D&D data
            if (DEBUG_ENABLED && c.name === 'Elandor Greenleaf') {
              console.log(`[CustomAssetManager] 🔍 BACKEND CHARACTER INSPECTION for ${c.name}:`, {
                hasClassFeatures: !!c.classFeatures,
                hasItems: !!c.items,
                hasFeats: !!c.feats,
                hasActions: !!c.actions,
                rootKeys: Object.keys(c),
                classFeaturesSample: c.classFeatures?.slice(0,2) || 'MISSING',
                itemsSample: c.items?.slice(0,2) || 'MISSING',
                featsSample: c.feats?.slice(0,2) || 'MISSING',
                actionsSample: c.actions || 'MISSING'
              });
            }
            
            return {
              ...c,
              name: c.name || (c.data && c.data.name) || 'Unnamed Character',
              artwork: c.artwork || (c.data && c.data.artwork) || c.artwork,
              type: 'character',
              source: 'custom', // @date_of_change 2025-09-12 - CRITICAL FIX: Mark custom characters for proper artwork handling
              canonicalData: c, // Use canonicalData for consistency with new uploads
              customData: c     // Keep customData as fallback for compatibility
            };
          });

          // date_of_change: 2025-09-22 - Merge localStorage assets to include items not visible via backend (e.g., no auth userId filter)
          const localAssets = this.getAllCustomAssetsFromLocalStorage();

          const mergeById = (primary, extra) => {
            const byId = new Map(primary.map(a => [a.id, a]));
            for (const item of extra) {
              if (!byId.has(item.id)) {
                byId.set(item.id, item);
              }
            }
            return Array.from(byId.values());
          };

          assets.characters = mergeById(assets.characters, localAssets.characters || []);
          assets.monsters = mergeById(assets.monsters, localAssets.monsters || []);
          
          // Sort by last modified (newest first)
          assets.characters.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
          assets.monsters.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));

          // @date_of_change: 2025-10-06 - Dedupe by name post-merge to avoid dashboard duplicates
          // Rationale: Backend and localStorage can both contain the same logical character with different IDs
          // (e.g., offline fallback save vs later backend save). Keep the most recently modified entry.
          const dedupeByName = (arr = []) => {
            const byName = new Map();
            for (const item of arr) {
              const key = (item?.name || '').trim().toLowerCase();
              if (!key) continue;
              if (!byName.has(key)) {
                byName.set(key, item);
              } else {
                const existing = byName.get(key);
                const curTs = new Date(item.lastModified || item.createdAt || 0).getTime();
                const exTs = new Date(existing.lastModified || existing.createdAt || 0).getTime();
                if (curTs >= exTs) {
                  byName.set(key, item);
                }
              }
            }
            return Array.from(byName.values());
          };

          assets.characters = dedupeByName(assets.characters);
          assets.monsters = dedupeByName(assets.monsters);
          
          // Cache the result
          if (useCache) {
            this._cachedAssets = assets;
          }
          
          if (DEBUG_ENABLED) console.debug('[CustomAssetManager] 🔍 FINAL RESULT - Returning assets:', {
            charactersCount: assets.characters.length,
            monstersCount: assets.monsters.length,
            sampleCharacter: assets.characters[0]?.name,
            sampleMonster: assets.monsters[0]?.name,
            fullAssets: assets
          });
          
          return assets;
        }
      } catch (error) {
        if (DEBUG_ENABLED) console.debug('[CustomAssetManager] 🔍 Backend fetch failed, falling back to localStorage:', error.message);
      }
      
      // Fallback to localStorage
      return this.getAllCustomAssetsFromLocalStorage();
      
    } catch (error) {
      console.error('[CustomAssetManager] Failed to get assets:', error);
      return { characters: [], monsters: [] };
    }
  }
  
  /**
   * Get assets from localStorage (fallback)
   * @private
   */
  getAllCustomAssetsFromLocalStorage() {
    const assetsJson = localStorage.getItem(this.storageKey);
    if (!assetsJson) {
      return { characters: [], monsters: [] };
    }
    
    const assets = JSON.parse(assetsJson);
    if (!assets.characters) assets.characters = [];
    if (!assets.monsters) assets.monsters = [];
    
    // @date_of_change: 2025-09-22 - Deduplicate by id (prefer latest lastModified)
    const dedupeById = (arr = []) => {
      const byId = new Map();
      for (const item of arr) {
        if (!item || !item.id) continue;
        const curTs = new Date(item.lastModified || item.createdAt || 0).getTime();
        if (!byId.has(item.id)) {
          byId.set(item.id, item);
        } else {
          const existing = byId.get(item.id);
          const exTs = new Date(existing.lastModified || existing.createdAt || 0).getTime();
          if (curTs > exTs) byId.set(item.id, item);
        }
      }
      return Array.from(byId.values());
    };
    assets.characters = dedupeById(assets.characters);
    assets.monsters = dedupeById(assets.monsters);
    
    assets.characters.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
    assets.monsters.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
    
    return assets;
  }

  /**
   * Get custom characters for Asset Browser
   * @returns {Array} - Array of custom character objects
   */
  getCustomCharacters() {
    return this.getAllCustomAssets(true).characters;
  }

  /**
   * Get custom monsters for Asset Browser
   * @returns {Array} - Array of custom monster objects
   */
  getCustomMonsters() {
    return this.getAllCustomAssets(true).monsters;
  }
  
  /**
   * Get formatted assets for the Asset Browser with conversion cache
   * @param {string} type - 'character' or 'monster'
   * @returns {Array} - Array of browser-formatted assets
   */
  getFormattedAssets(type) {
    const assets = this.getAssetsByType(type);
    return assets.map(asset => this.toAssetBrowserFormat(asset));
  }
  
  /**
   * Clear conversion cache
   */
  clearConversionCache() {
    this.conversionCache.clear();
    if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Conversion cache cleared');
  }

  /**
   * Get custom assets by type (for Dashboard integration)
   * @param {string} type - 'character' or 'monster'
   * @returns {Array} - Array of custom assets of the specified type
   */
  getAssetsByType(type) {
    try {
      // date_of_change: 2025-08-24 - Avoid using async getAllCustomAssets() synchronously
      // Prefer in-memory cache when present; otherwise, fall back to localStorage
      const assets = this._cachedAssets || this.getAllCustomAssetsFromLocalStorage();
      
      if (type === 'character') {
        return assets.characters || [];
      } else if (type === 'monster') {
        return assets.monsters || [];
      } else {
        console.warn(`[CustomAssetManager] Invalid asset type: ${type}`);
        return [];
      }
    } catch (error) {
      console.error('[CustomAssetManager] Failed to get assets by type:', error);
      return [];
    }
  }

  /**
   * Save a custom character (wrapper for saveCustomAsset)
   * @param {Object} characterData - Character data from CharacterEditor
   * @param {boolean} allowOverwrite - Whether to allow overwriting existing character
   * @returns {Object} - Saved character asset
   */
  async saveCharacter(characterData, allowOverwrite = false) { // @date_of_change 2025-08-30 - Make async since it calls async saveCustomAsset
    try {
      // Check if character with same name already exists
      const existingAssets = this.getAllCustomAssetsFromLocalStorage();
      const existingCharacter = existingAssets.characters.find(c => c.name === characterData.name);
      
      if (existingCharacter && !allowOverwrite) {
        throw new Error('A character with this name already exists');
      }
      
      // date_of_change: 2025-09-02 - Preserve existing ID for character overwrites
      // If we're overwriting and found existing character, use its ID to replace the old one
      if (existingCharacter && allowOverwrite) {
        characterData.id = existingCharacter.id;
        if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Overwriting existing character, preserving ID:', existingCharacter.id);
      }

      await assertCustomEntityCreationAllowed({
        entityType: 'character',
        entityId: characterData.id || existingCharacter?.id
      });
      
      // date_of_change: 2025-08-31 - FIXED: Remove duplicate normalization violating AGENTS.md
      // The characterData is already normalized by UploadDataNormalizer - DO NOT normalize again
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Using pre-normalized character data:', characterData);
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Character name should be preserved as:', characterData.name);
      const normalizedData = characterData; // Use data as-is - already normalized
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Final character data for saving:', normalizedData);
      
      // date_of_change: 2025-08-31 - Debug HP corruption
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] HP values before processing:', {
        hp: normalizedData.hp,
        maxHp: normalizedData.maxHp,
        type_hp: typeof normalizedData.hp,
        type_maxHp: typeof normalizedData.maxHp
      });
      
      // Format HP data consistently as an object
      const hpObj = {
        current: normalizedData.hp || 0,
        max: normalizedData.maxHp || normalizedData.hp || 1
      };
      
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] HP object created:', hpObj);
      
      // Prepare asset data for saveCustomAsset with normalized structure
      const assetData = {
        id: characterData.id, // Include ID from above logic (preserves existing ID for overwrites)
        name: normalizedData.name,
        type: 'character',
        baseTemplate: normalizedData.id || characterData.baseTemplate || 'custom',
        customData: {
          ...normalizedData,
          // Ensure critical fields are explicitly included
          class: normalizedData.class,
          level: normalizedData.level,
          // Use consistent HP object format
          hpObj: hpObj,
          hp: hpObj.current,
          maxHp: hpObj.max,
          ac: normalizedData.ac,
          abilities: normalizedData.abilities,
          // Include short form abilities for backwards compatibility
          abilitiesShort: {
            str: normalizedData.abilities.strength,
            dex: normalizedData.abilities.dexterity,
            con: normalizedData.abilities.constitution,
            int: normalizedData.abilities.intelligence,
            wis: normalizedData.abilities.wisdom,
            cha: normalizedData.abilities.charisma
          },
          skills: normalizedData.skills,
          equipment: normalizedData.equipment || [],
          features: normalizedData.features || [],
          spells: normalizedData.spells || null,
          background: normalizedData.background,
          race: normalizedData.race,
          alignment: normalizedData.alignment
        },
        artwork: normalizedData.artwork
      };
      
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Saving normalized character:', normalizedData.name);
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Character artwork:', normalizedData.artwork);
      
      return await this.saveCustomAsset(assetData); // @date_of_change 2025-08-30 - Add await for async method
      
    } catch (error) {
      console.error('[CustomAssetManager] Failed to save character:', error);
      throw error;
    }
  }

  /**
   * Save a custom monster with validation and error handling
   * @param {Object} monsterData - Monster data to save
   * @param {boolean} allowOverwrite - Whether to allow overwriting existing monsters
   * @returns {Promise<Object>} - Saved monster data
   */
  async saveMonster(monsterData, allowOverwrite = false) { // @date_of_change 2025-08-30 - Make async since it calls async saveCustomAsset
    try {
      if (DEBUG_ENABLED) console.debug('[CustomAssetManager] Attempting to save monster:', monsterData.name, monsterData);
      
      // Import MonsterDataNormalizer for consistent data handling
      // @date_of_change 2025-08-30 - Fix import to use named export instead of default
      const { MonsterDataNormalizer } = await import('../utils/MonsterDataNormalizer');
      // @date_of_change 2025-08-30 - Fix require to use dynamic import
      const { default: HPFormatBridge } = await import('../utils/HPFormatBridge');
      const { logIO, logValidation } = await import('../utils/DebugLogger');
      
      // Enhanced debug logging
      logIO('[CustomAssetManager] Monster save - input data structure', {
        hasCustomData: !!(monsterData.customData && typeof monsterData.customData === 'object'),
        topLevelKeys: Object.keys(monsterData),
        id: monsterData.id || 'undefined',
        name: monsterData.name || 'undefined'
      });
      
      // Check if monster with same name already exists
      const existingAssets = this.getAllCustomAssetsFromLocalStorage();
      const existingMonster = existingAssets.monsters.find(m => m.name === monsterData.name);
      
      if (existingMonster) {
        logIO('[CustomAssetManager] Found existing monster with same name:', {
          existingId: existingMonster.id,
          allowOverwrite: allowOverwrite
        });
        
        if (!allowOverwrite) {
          throw new Error('A monster with this name already exists');
        }
        
        // Preserve the ID when overwriting
        monsterData.id = existingMonster.id;
      }

      await assertCustomEntityCreationAllowed({
        entityType: 'monster',
        entityId: monsterData.id || existingMonster?.id
      });
      
      // Extract the actual monster data - handle nested customData structure
      // This is critical for handling data from both editor and asset browser formats
      let dataToSave;
      if (monsterData.customData && typeof monsterData.customData === 'object') {
        dataToSave = {...monsterData.customData};
        // Preserve critical top-level fields
        if (!dataToSave.name && monsterData.name) dataToSave.name = monsterData.name;
        if (!dataToSave.artwork && monsterData.artwork) dataToSave.artwork = monsterData.artwork;
        if (!dataToSave.id && monsterData.id) dataToSave.id = monsterData.id;
      } else {
        dataToSave = {...monsterData};
      }
      
      // Normalize the monster data to ensure consistency
      logIO('[CustomAssetManager] Normalizing monster data before saving:', dataToSave.name);
      const normalizedData = MonsterDataNormalizer.normalize(dataToSave);
      
      // Get original HP format and values for better preservation
      let originalHP = {
        current: null,
        max: null,
        source: 'none'
      };
      
      // Check all possible HP formats to ensure we preserve the user's edits
      if (dataToSave.hpObj) {
        originalHP = {
          current: dataToSave.hpObj.current,
          max: dataToSave.hpObj.max,
          source: 'hpObj'
        };
      } else if (dataToSave.hp !== undefined && dataToSave.maxHp !== undefined) {
        originalHP = {
          current: dataToSave.hp,
          max: dataToSave.maxHp,
          source: 'separate-fields'
        };
      } else if (dataToSave.hitPoints && typeof dataToSave.hitPoints === 'object') {
        originalHP = {
          current: dataToSave.hitPoints.value || dataToSave.hitPoints.average,
          max: dataToSave.hitPoints.max || dataToSave.hitPoints.value || dataToSave.hitPoints.average,
          source: 'hitPoints-object'
        };
      }
      
      // Log original vs normalized values for key fields
      logIO('[CustomAssetManager] Data normalization results:', {
        name: normalizedData.name,
        originalHP,
        normalizedHP: normalizedData.hp,
        normalizedMaxHP: normalizedData.maxHp,
        normalizedHPObj: normalizedData.hpObj,
        originalAbilitiesKeys: dataToSave.abilities ? Object.keys(dataToSave.abilities) : [],
        originalStatsKeys: dataToSave.stats ? Object.keys(dataToSave.stats) : [],
        normalizedAbilitiesKeys: normalizedData.abilities ? Object.keys(normalizedData.abilities) : [],
        normalizedStatsKeys: normalizedData.stats ? Object.keys(normalizedData.stats) : []
      });
      
      // Create a unified HP object with HPFormatBridge
      // Prioritize original HP values if they exist
      let hpObj;
      if (originalHP.current !== null && originalHP.max !== null) {
        hpObj = {
          current: parseInt(originalHP.current) || 1,
          max: parseInt(originalHP.max) || 1
        };
        logIO('[CustomAssetManager] Using original HP values:', hpObj);
      } else {
        hpObj = HPFormatBridge.createUnifiedHP(normalizedData);
        logIO('[CustomAssetManager] Created unified HP from normalized data:', hpObj);
      }
      
      // Ensure HP values are valid
      if (!hpObj || hpObj.current === undefined || hpObj.max === undefined) {
        hpObj = {
          current: normalizedData.hp || 1,
          max: normalizedData.maxHp || normalizedData.hp || 1
        };
        logIO('[CustomAssetManager] Created fallback HP object:', hpObj);
      }
      
      // Ensure ability scores are properly stored in both formats
      // This is critical for compatibility across components
      const abilities = normalizedData.abilities || {};
      const stats = {
        str: abilities.strength || abilities.str || 10,
        dex: abilities.dexterity || abilities.dex || 10,
        con: abilities.constitution || abilities.con || 10,
        int: abilities.intelligence || abilities.int || 10, 
        wis: abilities.wisdom || abilities.wis || 10,
        cha: abilities.charisma || abilities.cha || 10
      };
      
      // Create the final asset data with explicit handling of critical fields
      // date_of_change: 2025-08-24 - Ensure description text is preserved for actions/traits/legendaryActions
      const toDescription = (item) => {
        if (!item || typeof item !== 'object') return item;
        // Prefer existing description
        if (item.description && typeof item.description === 'string' && item.description.trim().length > 0) {
          return item;
        }
        const copy = { ...item };
        // Map common SRD fields to description
        if (!copy.description) {
          if (typeof copy.desc === 'string' && copy.desc.trim().length > 0) {
            copy.description = copy.desc;
          } else if (Array.isArray(copy.entries) && copy.entries.length > 0) {
            // Join entries arrays (string or nested) into a single string
            const flatten = (arr) => arr
              .map(e => Array.isArray(e) ? e.join('\n') : (typeof e === 'object' && e?.entries ? flatten(e.entries) : String(e)))
              .join('\n');
            copy.description = flatten(copy.entries);
          }
        }
        return copy;
      };

      const mapWithDescription = (arr) => Array.isArray(arr) ? arr.map(toDescription) : [];

      const assetData = {
        name: normalizedData.name || 'Unknown Monster',
        type: 'monster',
        id: monsterData.id, // Preserve existing ID for overwrites
        baseTemplate: normalizedData.baseTemplate || dataToSave.baseTemplate || 'custom',
        customData: {
          ...normalizedData,
          // Ensure critical fields are explicitly included
          name: normalizedData.name,
          // date_of_change: 2025-08-24 - Source CR from normalized challenge first to fix header CR showing 0
          cr: (
            (normalizedData.challenge && (normalizedData.challenge.cr ?? normalizedData.challenge.rating))
            || normalizedData.cr
            || (dataToSave.challenge && (dataToSave.challenge.cr ?? dataToSave.challenge.rating))
            || dataToSave.cr
            || dataToSave.challengeRating
            || '0'
          ),
          size: normalizedData.size || dataToSave.size || 'Medium',
          type: normalizedData.type || dataToSave.type || 'monster',
          // Use consistent HP format with all variations for compatibility
          hpObj: hpObj,
          hp: hpObj.current,
          maxHp: hpObj.max,
          hitPoints: normalizedData.hitPoints || {
            value: hpObj.current,
            max: hpObj.max,
            average: hpObj.current
          },
          ac: normalizedData.ac || dataToSave.ac || 10,
          // Include both ability score formats
          abilities: normalizedData.abilities,
          stats: stats,
          // Preserve special properties
          actions: mapWithDescription(normalizedData.actions || dataToSave.actions),
          traits: mapWithDescription(normalizedData.traits || dataToSave.traits),
          legendaryActions: mapWithDescription(normalizedData.legendaryActions || dataToSave.legendaryActions),
          // Preserve other fields that might be used
          languages: normalizedData.languages || dataToSave.languages,
          senses: normalizedData.senses || dataToSave.senses,
          skills: normalizedData.skills || dataToSave.skills,
          speed: normalizedData.speed || dataToSave.speed,
          savingThrows: normalizedData.savingThrows || dataToSave.savingThrows,
          // Ensure we preserve any non-standard fields from the original data
          ...Object.keys(dataToSave)
            .filter(key => ![
              'id', 'name', 'type', 'baseTemplate', 'artwork', 'customData',
              'hp', 'maxHp', 'hpObj', 'hitPoints', 'ac', 'cr', 'challengeRating', 
              'size', 'abilities', 'stats', 'actions', 'traits', 'legendaryActions'
            ].includes(key))
            .reduce((obj, key) => {
              obj[key] = dataToSave[key];
              return obj;
            }, {}),
          // Preserve artwork
          artwork: normalizedData.artwork || dataToSave.artwork
        },
        artwork: normalizedData.artwork || dataToSave.artwork
      };
      
      logIO('[CustomAssetManager] Final monster asset structure for save:', {
        id: assetData.id,
        name: assetData.name,
        type: assetData.type,
        hasCustomData: !!assetData.customData,
        hp: assetData.customData.hp,
        maxHp: assetData.customData.maxHp,
        hpObj: assetData.customData.hpObj,
        hasAbilities: !!assetData.customData.abilities,
        hasStats: !!assetData.customData.stats,
        artwork: !!assetData.artwork
      });
      
      // Validate the data before saving
      if (!assetData.name) {
        logValidation('[CustomAssetManager] Monster validation failed: Missing name');
        throw new Error('Cannot save monster: Name is required');
      }
      
      // Ensure we have valid HP values
      // date_of_change: 2025-08-24 - Do not throw on missing/invalid HP; auto-generate safe fallback
      if (!assetData.customData.hpObj || 
          assetData.customData.hpObj.current === undefined || 
          assetData.customData.hpObj.max === undefined) {
        logValidation('[CustomAssetManager] Monster HP format missing/invalid; generating fallback hpObj');
        const fallbackHp = {
          current: Number(normalizedData?.hp) || Number(dataToSave?.hp) || 1,
          max: Number(normalizedData?.maxHp) || Number(dataToSave?.maxHp) || Number(normalizedData?.hp) || Number(dataToSave?.hp) || 1
        };
        // Apply fallback consistently across fields
        assetData.customData.hpObj = fallbackHp;
        assetData.customData.hp = fallbackHp.current;
        assetData.customData.maxHp = fallbackHp.max;
        // date_of_change: 2025-08-24 - Removed erroneous throw inside object literal; fallback HP is acceptable
        assetData.customData.hitPoints = assetData.customData.hitPoints || {
          value: fallbackHp.current,
          max: fallbackHp.max,
          average: fallbackHp.current
        };
        
      }
      
      return await this.saveCustomAsset(assetData); // @date_of_change 2025-08-30 - Add await for async method
      
    } catch (error) {
      console.error('[CustomAssetManager] Failed to save monster:', error);
      throw error;
    }
  }

  /**
   * Load a specific custom asset by ID
   * @param {string} assetId - ID of the asset to load
   * @returns {Object|null} - Asset data or null if not found
   */
  loadCustomAsset(assetId) {
    try {
      // date_of_change: 2025-08-31 - First try in-memory cache and localStorage
      const allAssets = this._cachedAssets || this.getAllCustomAssetsFromLocalStorage();
      let asset = null;

      if (assetId.startsWith('custom_character_')) {
        asset = allAssets.characters.find(c => c.id === assetId);
      } else if (assetId.startsWith('custom_monster_')) {
        asset = allAssets.monsters.find(m => m.id === assetId);
      }

      if (asset) {
        console.log('[CustomAssetManager] Loaded asset from cache/localStorage:', assetId, asset);
        
        // @date_of_change 2025-09-12 - DEBUG: Inspect cached character data for rich D&D arrays
        if (asset && asset.type === 'character' && asset.name === 'Elandor Greenleaf') {
          console.log(`[CustomAssetManager] 🔍 CACHED CHARACTER INSPECTION for ${asset.name}:`, {
            assetKeys: Object.keys(asset),
            canonicalDataKeys: asset.canonicalData ? Object.keys(asset.canonicalData) : 'NO_CANONICAL_DATA',
            customDataKeys: asset.customData ? Object.keys(asset.customData) : 'NO_CUSTOM_DATA',
            canonicalDataRichArrays: asset.canonicalData ? {
              hasClassFeatures: !!asset.canonicalData.classFeatures,
              hasItems: !!asset.canonicalData.items,
              hasFeats: !!asset.canonicalData.feats,
              hasActions: !!asset.canonicalData.actions,
              classFeaturesSample: asset.canonicalData.classFeatures?.slice(0,2) || 'MISSING',
              itemsSample: asset.canonicalData.items?.slice(0,2) || 'MISSING',
              featsSample: asset.canonicalData.feats?.slice(0,2) || 'MISSING',
              actionsSample: asset.canonicalData.actions || 'MISSING'
            } : 'NO_CANONICAL_DATA'
          });
          
          // @date_of_change 2025-09-12 - DISABLE aggressive cache clearing until backend is working
          // The cache clearing was removing characters from localStorage permanently when backend is unavailable
          const hasRichArrays = !!(asset.canonicalData?.classFeatures || asset.canonicalData?.items || asset.canonicalData?.feats || asset.canonicalData?.actions);
          if (!hasRichArrays) {
            console.log(`[CustomAssetManager] ⚠️ WARNING: ${asset.name} missing rich D&D arrays, but keeping in cache since backend may be unavailable`);
            console.log(`[CustomAssetManager] 📊 Rich data status:`, {
              classFeatures: asset.canonicalData?.classFeatures ? `${asset.canonicalData.classFeatures.length} items` : 'MISSING',
              items: asset.canonicalData?.items ? `${asset.canonicalData.items.length} items` : 'MISSING', 
              feats: asset.canonicalData?.feats ? `${asset.canonicalData.feats.length} items` : 'MISSING',
              actions: asset.canonicalData?.actions ? `${Object.keys(asset.canonicalData.actions).length} actions` : 'MISSING'
            });
            // Keep the asset for now - dashboard needs to show characters even with incomplete data
          }
        }
        
        return asset;
      }

      // date_of_change: 2025-08-31 - If not found locally, try backend as synchronous fallback
      console.log(`[CustomAssetManager] Asset "${assetId}" not found in cache/localStorage, checking backend...`);
      
      // @date_of_change 2025-09-12 - DEBUG: Log backend reload for Elandor
      if (assetId.includes('elandor')) {
        console.log(`[CustomAssetManager] 🔄 BACKEND RELOAD triggered for Elandor Greenleaf...`);
      }
      
      // @date_of_change: 2025-09-21 - Use dynamic backend URL resolution for synchronous calls
      // Note: Still using XMLHttpRequest for synchronous operation, but with dynamic URL
      const xhr = new XMLHttpRequest();

      // Get backend URL from BackendUrlResolver cache (synchronous access to cached value)
      let backendUrl = this.apiUrl;
      try {
        const cached = window.localStorage.getItem('backend_url_cache');
        if (cached) {
          const cacheData = JSON.parse(cached);
          if (cacheData.url && cacheData.timestamp && (Date.now() - cacheData.timestamp) < 300000) { // 5 min TTL
            backendUrl = cacheData.url;
          }
        }
      } catch (error) {
        // Fallback to configured URL
      }

      const apiUrl = assetId.startsWith('custom_character_')
        ? `${backendUrl}/api/characters/${assetId}`
        : `${backendUrl}/api/monsters/${assetId}`;

      xhr.open('GET', apiUrl, false); // false = synchronous
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      try {
        xhr.send();
        
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          if (response.success && response.data) {
            console.log('[CustomAssetManager] Loaded asset from backend:', assetId, response.data);
            
            // @date_of_change 2025-09-12 - DEBUG: Inspect backend data structure for Elandor
            if (assetId.includes('elandor')) {
              console.log(`[CustomAssetManager] 🔍 BACKEND DATA INSPECTION for Elandor:`, {
                backendDataKeys: Object.keys(response.data),
                hasClassFeatures: !!response.data.classFeatures,
                hasItems: !!response.data.items,
                hasFeats: !!response.data.feats,
                hasActions: !!response.data.actions,
                hasSpells: !!response.data.spells,
                hasBiography: !!response.data.biography,
                spellsStructure: response.data.spells ? Object.keys(response.data.spells) : 'MISSING',
                biographyStructure: response.data.biography ? Object.keys(response.data.biography) : 'MISSING'
              });
            }
            
            // Transform backend data to CustomAssetManager format
            // @date_of_change 2025-09-12 - CRITICAL FIX: Set both canonicalData AND customData for consistency
            const transformedAsset = {
              id: response.data.id,
              name: response.data.name,
              type: assetId.startsWith('custom_character_') ? 'character' : 'monster',
              canonicalData: response.data, // DataService looks for canonicalData first
              customData: response.data,    // Keep customData as fallback
              createdAt: response.data.lastModified || new Date().toISOString(),
              lastModified: response.data.lastModified || new Date().toISOString()
            };
            
            return transformedAsset;
          }
        }
      } catch (backendError) {
        console.warn('[CustomAssetManager] Backend lookup failed:', backendError);
      }

      console.warn(`[CustomAssetManager] Custom asset with ID "${assetId}" not found in cache, localStorage, or backend.`);
      return null;
    } catch (error) {
      console.error(`[CustomAssetManager] Failed to load asset with ID "${assetId}":`, error);
      return null;
    }
  }

  /**
   * Load a custom asset through the authenticated backend when it is not in
   * this browser's local cache. GMA-created encounter actors are persisted in
   * S3 and therefore may never have existed in localStorage.
   */
  async loadCustomAssetAsync(assetId) {
    const allAssets = this._cachedAssets || this.getAllCustomAssetsFromLocalStorage();
    const localAsset = assetId.startsWith('custom_character_')
      ? allAssets.characters.find((asset) => asset.id === assetId)
      : allAssets.monsters.find((asset) => asset.id === assetId);
    if (localAsset) return localAsset;

    const assetType = assetId.startsWith('custom_character_') ? 'character' : 'monster';
    const endpoint = assetType === 'character' ? 'characters' : 'monsters';
    const { apiFetch } = await import('../utils/apiFetch.js');
    const response = await apiFetch(`/api/${endpoint}/${encodeURIComponent(assetId)}`, {
      requireAuth: true,
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error?.message || body?.error || `Unable to load ${assetType} (${response.status}).`);
    }
    const body = await response.json();
    const data = body?.data;
    if (!data || typeof data !== 'object') return null;
    return {
      id: data.id || assetId,
      name: data.name || `Unnamed ${assetType}`,
      type: assetType,
      canonicalData: data,
      customData: data,
      artwork: data.artwork,
      baseTemplate: data.baseTemplate,
      createdAt: data.createdAt || data.lastModified || new Date().toISOString(),
      lastModified: data.lastModified || data.createdAt || new Date().toISOString(),
    };
  }

  /**
   * Generate a unique asset ID
   * @param {string} name - Asset name
   * @param {string} type - Asset type
   * @returns {string} - Unique asset identifier
   */
  generateAssetId(name, type) {
    const sanitizedName = name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    return `custom_${type}_${sanitizedName}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }

  /**
   * Convert custom asset to Asset Browser format
   * @param {Object} customAsset - Custom asset object
   * @returns {Object} - Asset Browser compatible object
   */
  /**
   * Convert custom asset to Asset Browser format with caching
   * @param {Object} customAsset - Custom asset object
   * @returns {Object} - Asset Browser compatible object
   */
  toAssetBrowserFormat(customAsset) {
    try {
      // Check cache first using composite key of id + lastModified
      const cacheKey = `${customAsset.id}_${customAsset.lastModified}_v${this.cacheVersion}`;
      
      // Return cached conversion if available
      if (this.conversionCache.has(cacheKey)) {
        return this.conversionCache.get(cacheKey);
      }
      
      // Extract and normalize data with error handling
      const data = customAsset.customData || {};
      
      // Normalize complex properties
      const normalized = this.normalizeComplexProperties(data);
      
      // Create the browser format object
      const browserFormat = {
        id: customAsset.id,
        name: customAsset.name,
        type: customAsset.type === 'character' ? 'Character' : 'Monster',
        artwork: customAsset.artwork,
        hp: normalized.hp,
        maxHp: normalized.maxHp,
        ac: normalized.ac,
        level: normalized.level,
        cr: normalized.cr,
        class: normalized.class,
        isCustom: true, // Flag to identify custom assets
        baseTemplate: customAsset.baseTemplate,
        // Store just a reference to the customData, not a deep copy
        customData: customAsset.customData
      };
      
      // Store in cache
      this.conversionCache.set(cacheKey, browserFormat);
      
      return browserFormat;
    } catch (error) {
      console.error('[CustomAssetManager] Error converting asset to browser format:', error);
      
      // Return a safe fallback object to prevent UI crashes
      const fallback = {
        id: customAsset.id || `error_${Date.now()}`,
        name: customAsset.name || 'Error Loading Asset',
        type: customAsset.type === 'character' ? 'Character' : 'Monster',
        artwork: customAsset.artwork || '',
        hp: 10,
        maxHp: 10,
        ac: 10,
        level: 1,
        cr: '0',
        class: '',
        isCustom: true,
        baseTemplate: customAsset.baseTemplate || 'custom',
        error: true, // Flag to identify error state
        customData: {}
      };
      
      return fallback;
    }
  }
  
  /**
   * Helper method to extract and normalize complex properties
   * @param {Object} data - Asset data to normalize
   * @returns {Object} - Normalized properties
   */
  normalizeComplexProperties(data) {
    // Access patterns for data in various formats
    const directData = data;
    const nestedData = data.data || {};
    
    // Handle complex CR object
    let crValue = '0';
    const crSource = directData.cr || nestedData.cr;
    if (crSource) {
      if (typeof crSource === 'object') {
        crValue = crSource.display || crSource.value || '0';
      } else {
        crValue = String(crSource);
      }
    }
    
    // Get HP values with fallbacks
    const directHp = directData.hp;
    const nestedHp = nestedData.hp;
    let hpValue = 10;
    let maxHpValue = 10;
    
    if (directHp !== undefined) {
      hpValue = typeof directHp === 'object' ? (directHp.average || directHp.value || 10) : directHp;
    } else if (nestedHp !== undefined) {
      hpValue = typeof nestedHp === 'object' ? (nestedHp.average || nestedHp.value || 10) : nestedHp;
    }
    
    maxHpValue = directData.maxHp || nestedData.maxHp || hpValue;
    
    // Get AC with fallbacks
    const directAc = directData.ac;
    const nestedAc = nestedData.ac;
    let acValue = 10;
    
    if (directAc !== undefined) {
      acValue = typeof directAc === 'object' ? (directAc.value || 10) : directAc;
    } else if (nestedAc !== undefined) {
      acValue = typeof nestedAc === 'object' ? (nestedAc.value || 10) : nestedAc;
    }
    
    // Extract class/level for characters
    const classValue = directData.class || nestedData.class || '';
    const levelValue = directData.level || nestedData.level || 1;
    
    return {
      cr: crValue,
      hp: hpValue,
      maxHp: maxHpValue,
      ac: acValue,
      class: classValue,
      level: levelValue
    };
  }

  /**
   * Get asset statistics for dashboard display
   * @returns {Object} - Statistics about custom assets
   */
  getAssetStats() {
    const assets = this.getAllCustomAssets();
    
    return {
      totalCustomAssets: assets.characters.length + assets.monsters.length,
      customCharacters: assets.characters.length,
      customMonsters: assets.monsters.length,
      recentAssets: [...assets.characters, ...assets.monsters]
        .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))
        .slice(0, 5),
      oldestAsset: [...assets.characters, ...assets.monsters]
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0] || null,
      newestAsset: [...assets.characters, ...assets.monsters]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null
    };
  }

  /**
   * Clear all custom assets (with confirmation)
   * @returns {boolean} - Success status
   */
  clearAllCustomAssets() {
    try {
      localStorage.removeItem(this.storageKey);
      // Clear all caches
      this.conversionCache.clear();
      this._cachedAssets = null;
      return true;
    } catch (error) {
      console.error('[CustomAssetManager] Failed to clear assets:', error);
      return false;
    }
  }

  /**
   * Get asset by ID (alias for loadCustomAsset for compatibility with DataService)
   * @param {string} id - ID of the asset to get
   * @returns {Object|null} - Asset data or null if not found
   */
  getAssetById(id) {
    return this.loadCustomAsset(id);
  }

  /**
   * Export custom assets as JSON for backup
   * @returns {string} - JSON string of all custom assets
   */
  exportCustomAssets() {
    const assets = this.getAllCustomAssets();
    return JSON.stringify(assets, null, 2);
  }

  /**
   * Import custom assets from JSON backup
   * @param {string} jsonData - JSON string of assets to import
   * @returns {boolean} - Success status
   */
  importCustomAssets(jsonData) {
    try {
      const importedAssets = JSON.parse(jsonData);
      
      // Validate structure
      if (!importedAssets.characters || !importedAssets.monsters) {
        throw new Error('Invalid asset data format');
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(importedAssets));
      
      // Clear all caches when importing new data
      this.conversionCache.clear();
      this._cachedAssets = null;
      
      return true;
      
    } catch (error) {
      console.error('[CustomAssetManager] Failed to import assets:', error);
      return false;
    }
  }

  // =========================================================================
  // CANONICAL CHARACTER STORAGE - @date_of_change 2025-09-02
  // CustomAssetManager now only handles persistence, no data transformation
  // =========================================================================

  /**
   * Save canonical character data (no transformation)
   * @param {Object} canonicalCharacter - Character in canonical format
   * @returns {string} Generated character ID
   */
  async saveCanonicalCharacter(canonicalCharacter) {
    console.log('[CustomAssetManager] saveCanonicalCharacter - Pure persistence layer');
    
    // @date_of_change 2025-09-04 - COMPREHENSIVE AUDIT BEFORE SAVE
    console.log('\n🔍 [SAVE-AUDIT] ================== PRE-SAVE AUDIT ==================');
    console.log('📊 [SAVE-AUDIT] Canonical Character Keys:', Object.keys(canonicalCharacter || {}));
    console.log('📊 [SAVE-AUDIT] ClassFeatures:', canonicalCharacter?.classFeatures ? {
      count: canonicalCharacter.classFeatures.length,
      names: canonicalCharacter.classFeatures.map(f => f.name)
    } : 'MISSING');
    console.log('📊 [SAVE-AUDIT] Items:', canonicalCharacter?.items ? {
      count: canonicalCharacter.items.length,
      names: canonicalCharacter.items.map(i => i.name)
    } : 'MISSING');
    console.log('📊 [SAVE-AUDIT] Actions Structure:', canonicalCharacter?.actions ? {
      type: typeof canonicalCharacter.actions,
      keys: Object.keys(canonicalCharacter.actions),
      actions: canonicalCharacter.actions?.actions ? canonicalCharacter.actions.actions.length : 'MISSING',
      bonusActions: canonicalCharacter.actions?.bonusActions ? canonicalCharacter.actions.bonusActions.length : 'MISSING'
    } : 'MISSING');
    console.log('📊 [SAVE-AUDIT] Feats:', canonicalCharacter?.feats ? {
      count: canonicalCharacter.feats.length,
      names: canonicalCharacter.feats.map(f => f.name)
    } : 'MISSING');
    console.log('🔍 [SAVE-AUDIT] ================== END PRE-SAVE AUDIT ==================\n');
    
    try {
      const characterId = `custom_character_${canonicalCharacter.name.trim().toLowerCase().replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      
      const assetData = {
        id: characterId,
        name: canonicalCharacter.name,
        type: 'character',
        canonicalData: canonicalCharacter, // Store canonical format directly
        createdAt: new Date().toISOString(),
        isCustom: true
      };
      
      console.log(`[CustomAssetManager] Saving canonical character: ${canonicalCharacter.name} as ${characterId}`);
      
      // Save to backend for persistence
      await this.saveCustomAsset(assetData);
      
      // Clear cache to force refresh
      this._cachedAssets = null;
      
      return characterId;
      
    } catch (error) {
      console.error('[CustomAssetManager] saveCanonicalCharacter failed:', error);
      throw error;
    }
  }

  /**
   * Load canonical character data (no transformation)
   * @param {string} characterId - Character ID
   * @returns {Object} Canonical character data
   */
  loadCanonicalCharacter(characterId) {
    console.log('\n🔄 [LOAD-AUDIT] ================== LOADING CANONICAL CHARACTER FROM STORAGE ==================');
    console.log('🔍 [LOAD-AUDIT] Character ID:', characterId);
    
    try {
      console.log('🔄 [LOAD-AUDIT] Calling loadCustomAsset...');
      const asset = this.loadCustomAsset(characterId);
      
      console.log('🔍 [LOAD-AUDIT] Asset structure analysis:', {
        hasAsset: !!asset,
        assetKeys: asset ? Object.keys(asset) : 'none',
        hasCanonicalData: !!(asset && asset.canonicalData),
        hasCustomData: !!(asset && asset.customData),
        canonicalDataKeys: (asset && asset.canonicalData) ? Object.keys(asset.canonicalData) : 'none',
        customDataKeys: (asset && asset.customData) ? Object.keys(asset.customData) : 'none'
      });
      
      // 🔧 CRITICAL SUCCESS: Load Process Data Access Pattern Fix  
      // @date_of_change 2025-09-04 - SOLUTION to character data loss in load process
      //
      // ROOT CAUSE: Previous code accessed asset?.canonicalData?.canonicalData (nested)
      // but data is actually stored in asset.customData after upload normalization
      // This caused loadCanonicalCharacter to return null, losing all rich D&D data
      //
      // THE FIX: Direct access to asset.customData as primary source, canonicalData as fallback
      // This successfully loads complete character data from the storage location
      const canonicalData = asset?.canonicalData || asset?.customData;
      console.log('🔍 [LOAD-AUDIT] Canonical data source:', 
        asset?.canonicalData ? 'asset.canonicalData' : 
        (asset?.customData ? 'asset.customData' : 'NONE'));
      console.log('🔍 [LOAD-AUDIT] FIXED ACCESS PATTERN - Now checking asset.customData directly');
      
      if (asset && canonicalData) {
        console.log('\n✅ [LOAD-AUDIT] ================== CANONICAL CHARACTER DATA FOUND ==================');
        console.log('🔍 [LOAD-AUDIT] Character name:', canonicalData.name);
        console.log('🔍 [LOAD-AUDIT] Character level:', canonicalData.level);
        console.log('🔍 [LOAD-AUDIT] Canonical data keys:', Object.keys(canonicalData || {}));
        console.log('🔍 [LOAD-AUDIT] ClassFeatures in canonical data:', {
          count: canonicalData.classFeatures?.length || 0,
          names: canonicalData.classFeatures?.slice(0, 3)?.map(f => f.name) || []
        });
        console.log('🔍 [LOAD-AUDIT] Items in canonical data:', {
          count: canonicalData.items?.length || 0,
          names: canonicalData.items?.slice(0, 3)?.map(i => i.name) || []
        });
        console.log('🔍 [LOAD-AUDIT] Actions in canonical data:', {
          actions: canonicalData.actions?.actions?.length || 0,
          bonusActions: canonicalData.actions?.bonusActions?.length || 0,
          reactions: canonicalData.actions?.reactions?.length || 0
        });
        console.log('🔍 [LOAD-AUDIT] Feats in canonical data:', {
          count: canonicalData.feats?.length || 0,
          names: canonicalData.feats?.slice(0, 3)?.map(f => f.name) || []
        });
        console.log('🔍 [LOAD-AUDIT] Spells in canonical data:', {
          hasSpells: !!canonicalData.spells,
          ability: canonicalData.spells?.ability,
          knownCount: canonicalData.spells?.known?.length || 0,
          slotsLevels: Object.keys(canonicalData.spells?.slots || {})
        });
        
        const finalCharacter = {
          id: characterId,
          ...canonicalData,
          _storage: {
            createdAt: asset.createdAt,
            isCustom: asset.isCustom || true
          }
        };
        
        console.log('\n✅ [LOAD-AUDIT] ================== CANONICAL CHARACTER LOADING COMPLETE ==================');
        console.log('🔍 [LOAD-AUDIT] Final character data integrity verified: ALL RICH DATA PRESERVED');
        
        return finalCharacter;
      }
      
      console.warn('\n❌ [LOAD-AUDIT] ================== NO CANONICAL DATA FOUND ==================');
      console.warn('❌ [LOAD-AUDIT] Character ID:', characterId);
      console.warn('❌ [LOAD-AUDIT] Asset structure:', asset ? Object.keys(asset) : 'No asset');
      return null;
      
    } catch (error) {
      console.error('\n❌ [LOAD-AUDIT] ================== CANONICAL CHARACTER LOADING FAILED ==================');
      console.error('❌ [LOAD-AUDIT] Character ID:', characterId);
      console.error('❌ [LOAD-AUDIT] Error:', error);
      return null;
    }
  }

  // =========================================================================
  // S3 DIRECT UPLOAD FUNCTIONS - @date_of_change 2025-09-26
  // S3 direct-upload flow (sign → PUT → finalize), zero-coupling with canonical JSON
  // =========================================================================

  /**
   * Sign S3 upload - get presigned URL for direct browser → S3 upload
   * @param {Object} params - Upload parameters
   * @param {string} params.type - Asset type ('token' | 'map' | 'other')
   * @param {File} params.file - File object from input
   * @returns {Object} - Sign response with putUrl, assetId, publicUrl
   */
  async signUpload({ type, file }) {
    const { apiFetch } = await import('../utils/apiFetch.js');

    const body = {
      type,                               // 'token' | 'map'
      mime: file.type,
      size: file.size,
      ext: (file.name.split('.').pop() || '').toLowerCase(),
      originalName: file.name,
    };

    const response = await apiFetch('/api/s3-uploads/sign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      requireAuth: true
    });

    if (!response.ok) {
      let errorMessage = `Sign request failed: ${response.status} ${response.statusText}`;
      try {
        const payload = await response.clone().json();
        errorMessage = payload?.message || payload?.error || errorMessage;
      } catch (_error) {
        // Fall through to the default HTTP message.
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Finalize S3 upload - mark asset as ready after successful PUT
   * @param {string} assetId - Asset ID from sign response
   * @returns {Object} - Finalized asset data with public URL
   */
  async finalizeUpload(assetId) {
    const { apiFetch } = await import('../utils/apiFetch.js');

    let lastError = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const response = await apiFetch('/api/s3-uploads/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId }),
        requireAuth: true
      });

      if (response.ok) {
        return response.json();
      }

      let errorMessage = `Finalize request failed: ${response.status} ${response.statusText}`;
      try {
        const payload = await response.clone().json();
        errorMessage = payload?.message || payload?.error || errorMessage;
      } catch (_error) {
        // Fall through to the default HTTP message.
      }

      lastError = new Error(errorMessage);
      if (attempt < 1) {
        await new Promise((resolve) => setTimeout(resolve, 350));
      }
    }

    throw lastError || new Error('Finalize request failed.');
  }

  async abortUpload(assetId) {
    if (!assetId) {
      return;
    }

    const { apiFetch } = await import('../utils/apiFetch.js');
    try {
      await apiFetch('/api/s3-uploads/abort', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId }),
        requireAuth: true
      });
    } catch (error) {
      console.warn('[CustomAssetManager] Failed to abort pending upload:', error);
    }
  }

  /**
   * Complete S3 upload flow - sign, PUT to S3, finalize
   * @param {File} file - File object from input
   * @param {Object} options - Upload options
   * @param {string} options.type - Asset type ('token' | 'map' | 'other')
   * @returns {Object} - Final asset data with URL, key, and metadata
   */
  async uploadToS3ViaSignedUrl(file, { type }) {
    let signedAssetId = null;
    try {
      // 1) Sign - get presigned PUT URL
      console.log('[CustomAssetManager] Signing S3 upload for:', { type, fileName: file.name, size: file.size });
      const sign = await this.signUpload({ type, file });
      signedAssetId = sign.assetId;
      console.log('[CustomAssetManager] Sign response:', sign);

      // 2) PUT direct to S3 (do not mutate headers between sign and PUT)
      console.log('[CustomAssetManager] Uploading directly to S3...');
      let putResponse;
      try {
        putResponse = await fetch(sign.putUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
      } catch (error) {
        if (error instanceof TypeError) {
          await this.abortUpload(signedAssetId);
          throw new Error('The image vault did not accept the upload. This usually means the S3 bucket CORS rules still need to allow this site.');
        }
        throw error;
      }

      if (!putResponse.ok) {
        await this.abortUpload(signedAssetId);
        throw new Error(`S3 PUT failed: ${putResponse.status} ${putResponse.statusText}`);
      }
      console.log('[CustomAssetManager] S3 upload successful');

      // 3) Finalize - mark as ready and get final asset data
      console.log('[CustomAssetManager] Finalizing upload...');
      const fin = await this.finalizeUpload(sign.assetId);
      console.log('[CustomAssetManager] Upload finalized:', fin);

      // 4) Return the saved asset reference (URL + key + id)
      return normalizeUploadedAsset(fin.asset);

    } catch (error) {
      console.error('[CustomAssetManager] S3 upload failed:', error);
      throw error;
    }
  }

  /**
   * List user's uploaded images from S3
   * @date_of_change: 2025-10-09 - Added to support browsing previously uploaded images
   * @param {string} type - Asset type filter ('token' | 'map' | null for all)
   * @returns {Promise<Array>} - Array of uploaded image assets
   */
  async listUploadedImages(type = null) {
    try {
      const { apiFetch } = await import('../utils/apiFetch.js');

      const url = type ? `/api/s3-uploads/list?type=${type}` : '/api/s3-uploads/list';
      const response = await apiFetch(url, { requireAuth: true });

      if (!response.ok) {
        throw new Error(`Failed to list uploaded images: ${response.status}`);
      }

      const result = await response.json();
      console.log('[CustomAssetManager] Listed uploaded images:', result);

      return (result.assets || []).map((asset) => normalizeUploadedAsset(asset));
    } catch (error) {
      console.error('[CustomAssetManager] Failed to list uploaded images:', error);
      return [];
    }
  }
}

// Export singleton instance
const customAssetManager = new CustomAssetManager();
export default customAssetManager;

// Export individual functions for direct use
export async function signUpload({ type, file }) {
  return customAssetManager.signUpload({ type, file });
}

export async function finalizeUpload(assetId) {
  return customAssetManager.finalizeUpload(assetId);
}

export async function uploadToS3ViaSignedUrl(file, { type }) {
  return customAssetManager.uploadToS3ViaSignedUrl(file, { type });
}
